import { useState } from 'react';
import type { VehicleAlert } from '../hooks/useVehicleAlerts';
import type { Asset } from '../../../models/asset';
import { UptimeDrawer } from '../../shared';
import { EditOrdometerHourmeterDialog } from '../dialogs/editOrdometerHourmeterDialog';
import { canEditMetric, getSecondaryName, hasNumber, type MonitoringRow } from '../monitoringRow';
import { getMetricAge, isCriticalAlert, isSchedulable } from '../monitoringUtils';
import { STATUS_TONE, type IRowContext } from './MonitoringTable';
import AlertsTab from './detail/AlertsTab';
import ManualTab from './detail/ManualTab';
import ReviewsTab from './detail/ReviewsTab';
import { getMaintenanceGroupName } from './maintenanceGroupName';
import type { MonitoringPersona } from '../personaConfig';
import useFmText from './useFmText';
import './VehicleDetailDrawer.scss';

export type DetailTab = 'summary' | 'reviews' | 'manual' | 'alerts';

const TABS: DetailTab[] = ['summary', 'reviews', 'manual', 'alerts'];

interface IVehicleDetailDrawerProps {
    row: MonitoringRow;
    initialTab?: DetailTab;
    fullAsset: Asset | null;
    fullAssetLoading: boolean;
    context: IRowContext;
    alerts: VehicleAlert[];
    alertsLoading: boolean;
    canEditMaintenanceGroup: boolean;
    onClose: () => void;
    onSchedule: (row: MonitoringRow) => void;
    userRole: MonitoringPersona;
    onOpenCampaigns: (asset: Asset) => void;
    onOpenContract: (asset: Asset) => void;
    onEditMaintenanceGroup: (asset: Asset) => void;
    onMetricUpdated: (assetId: string) => void;
}

const VehicleDetailDrawer = ({
    row,
    initialTab = 'summary',
    fullAsset,
    fullAssetLoading,
    context,
    alerts,
    alertsLoading,
    canEditMaintenanceGroup,
    onClose,
    onSchedule,
    userRole,
    onOpenCampaigns,
    onOpenContract,
    onEditMaintenanceGroup,
    onMetricUpdated,
}: IVehicleDetailDrawerProps) => {
    const { t, formatNumber, formatDate, vehicleType, kmToNext, updatedAgo, formatMetric } = useFmText();
    const [tab, setTab] = useState<DetailTab>(initialTab);
    const status = context.getStatus(row);
    const next = row.nextMaintenance;
    const canSchedule = isSchedulable(status);
    const reviewCount = fullAsset?.schedulings?.previous?.length ?? 0;
    const criticalAlerts = alerts.filter(isCriticalAlert).length;

    const tabBadge = (key: DetailTab) => {
        if (key === 'alerts' && alerts.length) {
            return <span className={`fm-tab-badge${criticalAlerts ? ' fm-bad' : ''}`}>{alerts.length}</span>;
        }
        if (key === 'reviews' && reviewCount) {
            return <span className='fm-tab-badge'>{reviewCount}</span>;
        }
        return null;
    };

    const renderMetricTile = (key: 'odometer' | 'hourmeter', label: string, unit: string) => {
        const metric = row[key];
        const { days, stale } = getMetricAge(key, metric?.date);
        return (
            <div className='fm-kpi'>
                <div className='fm-kpi__head'>
                    <span>{label}</span>
                    {canEditMetric(metric) && (
                        <EditOrdometerHourmeterDialog type={key} assetId={row.id} onClose={onMetricUpdated} />
                    )}
                </div>
                <b className='fm-num'>{formatMetric(metric, unit)}</b>
                {days !== undefined && (
                    <small className={stale ? 'fm-stale' : ''}>
                        {stale && <span className='rioglyph rioglyph-warning-sign' />} {updatedAgo(days)}
                    </small>
                )}
            </div>
        );
    };

    const renderSummary = () => {
        const last = row.lastMaintenance;
        const hasService = context.hasService(row);
        const hasVolksTotal = context.hasVolksTotal(row);
        const lastNumber = last?.number;
        const kmLabel = kmToNext(next?.km);

        return (
            <>
                <section className={`fm-hero${status ? ` fm-hero--${status}` : ''}`}>
                    <div className='fm-hero__main'>
                        <span className='fm-hero__label'>{t('columns.prox')}</span>
                        <div className='fm-hero__title'>
                            <b>
                                {hasNumber(next?.number)
                                    ? t('table.reviewNumber', { number: next.number })
                                    : t('status.noPlan')}
                            </b>
                            {status && (
                                <span className={`fm-pill ${STATUS_TONE[status]}`}>{t(`status.${status}`)}</span>
                            )}
                        </div>
                        {kmLabel && <span className='fm-hero__km fm-num'>{kmLabel}</span>}
                    </div>
                    {canSchedule && (
                        <button type='button' className='fm-btn fm-primary' onClick={() => onSchedule(row)}>
                            {t('actions.scheduleReview')}
                        </button>
                    )}
                </section>

                <div className='fm-kpis'>
                    {renderMetricTile('odometer', t('columns.odo'), 'km')}
                    {renderMetricTile('hourmeter', t('columns.hor'), 'h')}
                    <div className='fm-kpi'>
                        <div className='fm-kpi__head'>
                            <span>{t('columns.ult')}</span>
                        </div>
                        <b>{hasNumber(lastNumber) ? t('table.reviewNumber', { number: lastNumber }) : '–'}</b>
                        <small>
                            {[last?.checkupType, last?.date ? formatDate(last.date) : ''].filter(Boolean).join(' · ')}
                        </small>
                    </div>
                </div>

                <section className='fm-section'>
                    <h3 className='fm-section-title'>{t('detail.servicesSection')}</h3>
                    <div className='fm-badges'>
                        <span className={`fm-pill ${row.warranty === 'in' ? 'fm-ok' : 'fm-mut'}`}>
                            <span className='rioglyph rioglyph-shield' />
                            {row.warranty
                                ? t(`warranty.${row.warranty}`)
                                : row.warrantyRaw || t('detail.noWarrantyInfo')}
                        </span>
                        <span className={`fm-pill ${row.deviceConnected ? 'fm-ok' : 'fm-mut'}`}>
                            <span
                                className={`rioglyph ${row.deviceConnected ? 'rioglyph-wifi' : 'rioglyph-wifi-off'}`}
                            />
                            {row.deviceConnected ? t('connection.online') : t('connection.offline')}
                        </span>
                        <span className={`fm-pill ${hasService ? 'fm-ok' : 'fm-mut'}`}>
                            <span className='rioglyph rioglyph-signal' />
                            {hasService ? row.connectivity || t('services.connected') : t('detail.noService')}
                        </span>
                        <span className={`fm-pill ${hasVolksTotal ? 'fm-info' : 'fm-mut'}`}>
                            <span className='rioglyph rioglyph-truck' />
                            {hasVolksTotal ? row.volksTotal || 'Volks|Total' : t('services.noVolksTotal')}
                        </span>
                    </div>
                    {row.hasContractDetails && fullAsset && (
                        <button type='button' className='fm-linkbtn' onClick={() => onOpenContract(fullAsset)}>
                            {t('detail.viewContract')}
                        </button>
                    )}
                </section>

                <div className='fm-shortcuts'>
                    <button
                        type='button'
                        className={`fm-shortcut${criticalAlerts ? ' fm-shortcut--bad' : ''}`}
                        onClick={() => setTab('alerts')}
                    >
                        <span className='rioglyph rioglyph-warning-sign fm-shortcut__icon' />
                        <span className='fm-shortcut__body'>
                            {alertsLoading ? (
                                <span className='fm-skel' style={{ width: 28, height: 18 }} />
                            ) : (
                                <b className='fm-num'>{formatNumber(alerts.length)}</b>
                            )}
                            <small>
                                {criticalAlerts
                                    ? t('detail.alertsCritical', { count: criticalAlerts })
                                    : t('detail.alertsLast7Days')}
                            </small>
                        </span>
                        <span className='rioglyph rioglyph-chevron-right' />
                    </button>
                    <button type='button' className='fm-shortcut' onClick={() => setTab('reviews')}>
                        <span className='rioglyph rioglyph-book fm-shortcut__icon' />
                        <span className='fm-shortcut__body'>
                            <b className='fm-num'>{fullAsset ? formatNumber(reviewCount) : '–'}</b>
                            <small>{t('detail.reviewsDone')}</small>
                        </span>
                        <span className='rioglyph rioglyph-chevron-right' />
                    </button>
                    <button
                        type='button'
                        className={`fm-shortcut${row.campaignCount ? ' fm-shortcut--warn' : ''}`}
                        disabled={!row.campaignCount || !fullAsset}
                        onClick={() => fullAsset && onOpenCampaigns(fullAsset)}
                    >
                        <span className='rioglyph rioglyph-calendar-today fm-shortcut__icon' />
                        <span className='fm-shortcut__body'>
                            <b className='fm-num'>{formatNumber(row.campaignCount)}</b>
                            <small>{t('detail.pendingCampaignsLabel')}</small>
                        </span>
                        {row.campaignCount > 0 && <span className='rioglyph rioglyph-chevron-right' />}
                    </button>
                </div>

                <section className='fm-section'>
                    <h3 className='fm-section-title'>{t('detail.registrationSection')}</h3>
                    <dl className='fm-dmeta'>
                        <dt>{t('detail.account')}</dt>
                        <dd>{context.getAccountName(row) ?? '–'}</dd>

                        <dt>{t('detail.model')}</dt>
                        <dd>{[vehicleType(row.type), fullAsset?.model].filter(Boolean).join(' · ') || '–'}</dd>

                        <dt>{t('columns.grupo')}</dt>
                        <dd>
                            {getMaintenanceGroupName(t, row.maintenanceGroup)}
                            {canEditMaintenanceGroup && fullAsset && (
                                <button
                                    type='button'
                                    className='fm-linkbtn'
                                    onClick={() => onEditMaintenanceGroup(fullAsset)}
                                >
                                    {t('detail.edit')}
                                </button>
                            )}
                        </dd>
                    </dl>
                </section>
            </>
        );
    };

    return (
        <UptimeDrawer
            className='fleet-monitoring'
            width={600}
            title={[row.licensePlate ?? row.chassis, getSecondaryName(row)].filter(Boolean).join(' · ')}
            subtitle={<span className='fm-mono fm-muted'>{row.chassis}</span>}
            onClose={onClose}
            footer={
                canSchedule && tab !== 'summary' ? (
                    <button type='button' className='fm-btn fm-primary' onClick={() => onSchedule(row)}>
                        {t('actions.scheduleReview')}
                    </button>
                ) : undefined
            }
        >
            <div className='fm-tabs' role='tablist'>
                {TABS.map(key => (
                    <button
                        type='button'
                        role='tab'
                        key={key}
                        aria-selected={tab === key}
                        className='fm-tab'
                        onClick={() => setTab(key)}
                    >
                        {t(`detail.tabs.${key}`)}
                        {tabBadge(key)}
                    </button>
                ))}
            </div>

            <div role='tabpanel' className='fm-tabpanel'>
                {tab === 'summary' && renderSummary()}
                {tab === 'reviews' && <ReviewsTab asset={fullAsset} loading={fullAssetLoading} userRole={userRole} />}
                {tab === 'manual' && <ManualTab chassis={row.chassis} />}
                {tab === 'alerts' && <AlertsTab alerts={alerts} loading={alertsLoading} />}
            </div>
        </UptimeDrawer>
    );
};

export default VehicleDetailDrawer;
